/**
 * Every user-facing string in the invite and first-run flow, and since #360 the sign-in surfaces'
 * (SI-D2 to SI-D8: the sign-in page, the refusal when an email already has an account), and since
 * #399 the consents' (the AI ask, the Privacy page, withdrawing, what the app says without a
 * consent, and the full texts: "Consent" below), and since #709 the analytics consent's (version 2 of
 * its text: "Consent: analytics" below).
 *
 * Transcribed from the first-run design's copy deck (§8). It lives here rather
 * than inline so that a screen cannot quietly invent copy, and so the em-dash
 * ban is enforceable by a test rather than by review attention.
 *
 * Placeholders are `{name}`-style and substituted with `fill()` below. No i18n
 * machinery: the app is English-only, and German is produced by the coach
 * BACKEND as message content, never as UI chrome.
 */
export const COPY = {
  // ── Invite page ──
  inviteEyebrow: 'GROUNDWORK',
  inviteHeadline: '{inviter} invited you to Groundwork.',
  inviteHeadlineFallback: "You've been invited to Groundwork.",
  inviteBody:
    'A training log with a coach that reads it. Free weights, your own numbers, no feed.',
  inviteCta: 'Continue with Apple',
  inviteExpiry: 'Your invite is open until {date}.',
  inviteUnknownTitle: "This invite link isn't valid.",
  inviteUnknownBody:
    "The code in this link doesn't match any invite. Check you copied the whole link; they're long and easy to truncate.",
  // No `{date}` here on purpose: `GET /api/invites/{code}` withholds
  // `expires_at` for every non-valid state, including this one (FR-D1) --
  // there is no date this page could ever fill in. The body string right
  // below already tells the reader why ("open for a limited time"), so this
  // stays a plain statement of what happened, not a repeat of the reason.
  inviteExpiredTitle: 'This invite has expired.',
  inviteExpiredBody:
    "Invites are open for a limited time so unused links don't stay live forever.",
  inviteRevokedTitle: 'This invite was withdrawn.',
  inviteRevokedBody: 'Whoever sent it turned it off.',
  inviteRedeemedTitle: 'This invite has already been used.',
  inviteRedeemedBody:
    'Each invite works exactly once. If that was you, sign in instead.',
  inviteRedeemedCta: 'Sign in',
  inviteAskAgain: 'Ask whoever sent it for a fresh one.',
  // Added in the final fix wave. The lookup can fail WITHOUT answering with a
  // state: `GET /api/invites/{code}` answers 429 above 60 attempts from one
  // address (api/routers/invites.py), and `useInviteStatus` sets
  // `retry: false`, so the page had `isLoading: false, data: undefined` and
  // shimmered forever. Deliberately NOT the `unknown` treatment: that says the
  // link is invalid, which would be a lie the reader acts on by throwing a
  // good invite away. This says the lookup failed and offers another go.
  inviteErrorTitle: "We couldn't check this invite.",
  inviteErrorBody:
    'Something went wrong looking it up, which is on us rather than on your link. Give it a moment and try again.',
  inviteErrorRetry: 'Try again',

  // ── First run, welcome (design 0b, FR-D18) ──
  // iOS-only for now: stage 3b is an iOS stage and the web flow has no
  // mascot-led shell yet. Carried here so the two decks stay one deck, and so
  // the web build already has the strings when its shell catches up.
  welcomeTitle: "Hi, I'm Yami Kuro.",
  // "Five", not "a few": the counter says `of 5` two taps later. It said FOUR
  // from when the picture step left first run (FR-D31) until F-D11 added the
  // band question and reversed that.
  welcomeBody: "I'm your coach. Five quick questions and I'll know how to read your training.",
  welcomeCta: "Let's go",

  // ── First run, shell ──
  frResume: 'Picking up where you left off.',
  frContinue: 'Continue',
  frBack: 'Back',
  // `{total}` as well as `{n}`: each client fills it from its own step count
  // (iOS: `FirstRunStep.allCases.count`; web: `TOTAL_STEPS`) rather than a
  // hardcoded number, so a step added or removed on either side can't leave
  // the string lying about how many there are. Both currently sit at five.
  frCounter: '{n} of {total}',
  frErrorSave: "Couldn't save that. Check your connection and try again.",

  // ── First run, steps ──
  // The five questions are SPOKEN by the mascot from a speech bubble (FR-D17),
  // so each is phrased as a person asking rather than as a page heading, and
  // the reason beneath the bubble is his sentence too, in the first person
  // (FR-D22). Must stay byte-identical to Copy.* on iOS.
  frNameTitlePrefilled: 'Good to meet you, {first}.',
  frNameReasonPrefilled:
    "I got this from your sign-in. Change it if it's not what you go by.",
  frNameEdit: 'Edit',
  frNameTitleEmpty: 'What should I call you?',
  // FR-D21: a first name only. `frNameLastLabel` is gone, and re-adding one
  // means re-adding a question nothing downstream needs.
  frNameReasonEmpty: "Just your first name. It's what I'll call you.",
  frNameFirstLabel: 'First name',

  frDobTitle: "When's your birthday?",
  frDobReason: "It's how I know what strong looks like for you.",
  // The reason card's VALUE line (FR-D24). It carries no explanation of its
  // own any more: the why sits directly beneath it, and saying it twice was
  // half of what made the first pass read like a datasheet.
  frDobDerived: '{age} years old',
  frDobErrorFuture: "That's in the future.",
  frDobErrorImplausible: "That doesn't look right. Check the year.",
  // Web-only, and legitimately outside the deck intersection: iOS asks this
  // question with a wheel `DatePicker`, which names its own columns. The web
  // asks it with three explicit controls because a native `<input type="date">`
  // renders in the BROWSER's locale -- `tt.mm.jjjj` on a German-configured
  // Chrome -- which no attribute can override, on a flow the project requires
  // to be English without exception. These three are the parts' accessible
  // names and the month/day placeholder text; none of them is visible as a
  // label, because the mascot has already asked the question out loud.
  frDobMonthLabel: 'Month',
  frDobDayLabel: 'Day',
  frDobYearLabel: 'Year',
  frDobYearPlaceholder: 'YYYY',

  frHeightTitle: 'How tall are you?',
  // The second sentence is a PROMISE, and it is why weight is not asked for in
  // first run: Health supplies it seconds later, and body_composition already
  // carries scale readings. Changing this line means changing that decision.
  frHeightReason: "One number, once. I'll never ask you to weigh yourself.",
  frHeightErrorRange: 'Enter a height between {min} and {max}.',
  // Web-only, added in Task 9's fix round, and legitimately outside the deck
  // intersection the same way frDobMonthLabel/frDobDayLabel/frDobYearLabel
  // are: iOS has no imperial ruler yet (Plan 3), so there is no counterpart
  // screen to keep these in step with. These are the two typable fields'
  // accessible names -- not visible as labels, since a short "ft"/"in" sits
  // beside each number instead and the mascot has already asked the question.
  frHeightFeetLabel: 'Feet',
  frHeightInchesLabel: 'Inches',

  frGenderTitle: "What's your gender?",
  frGenderReason:
    // Diverges from the design deck, deliberately, and must stay byte-identical
    // to Copy.frGenderReason on iOS. The deck said "Nothing in your training
    // numbers is calculated from it" -- true of the code today, and exactly the
    // wrong thing to promise, since the reason this is asked is that muscle is
    // built at different rates and hormonal cycles move recovery.
    "Muscle and recovery work differently, so this helps me read your numbers. Skip it if you'd rather.",
  frGenderMale: 'Man',
  frGenderFemale: 'Woman',
  frGenderNonBinary: 'Non-binary',
  frGenderPreferNotToSay: 'Prefer not to say',

  // ── First run, the band (F-D11) ──
  // A fifth step, reversing FR-D31 (which took first run from five steps to
  // four when the photo step left). frBandReason states the mechanism rather
  // than the word "skip": the skip affordance is frBandSkip, a separate
  // control below the primary action (design 3.4).
  frBandTitle: 'How often do you train?',
  // The real mechanism (F-D4, F-D15): it seeds what the coach assumes, and he
  // corrects it himself from what gets logged. That second half is what makes
  // skipping safe, so it is said rather than implied.
  frBandReason:
    "It's what I'll assume about how often you train. I'll correct it myself from what you actually log.",
  // The four names, final (F-D12). Do not improve them.
  frBandOccasional: 'Occasional',
  frBandSteady: 'Steady',
  frBandCommitted: 'Committed',
  frBandDedicated: 'Dedicated',
  // One plain cadence line per name (Bevel / Cal AI structure), anchored to
  // the 4 / 8 / 12 / 20 training-days-per-28 boundaries (F-D3).
  //
  // Steady/Committed/Dedicated CORRECTED in Task 7 (2026-08-24, follow-up to
  // Task 6's impeccable review): each was drafted as a range whose LOWER
  // bound was actually the band below's own real rate, not this band's --
  // "Once or twice a week" put occasional's ~1/week inside steady's option,
  // so someone training exactly twice a week (dead-center on steady's own
  // anchor, per band_for()) read the first-run question and picked
  // "Committed" instead. Each line now states only this band's own rate from
  // target_per_week() (api/signals/training_band.py): 1 / 2 / 3 / 5 sessions
  // a week. Single numbers rather than ranges, deliberately -- a range's
  // lower bound is exactly what collided with the neighbor last time.
  // Dedicated keeps "or more" because it has no ceiling anchor; occasional is
  // untouched (not flagged as wrong, and has no band below it to inherit a
  // number from).
  //
  // 4x/week has no line of its own -- it falls between Committed's 3 and
  // Dedicated's 5+. Considered, not missed: judged safe to ship because
  // someone at 4x/week most likely reads Committed, the band below their
  // true rate, which undercounts rather than overcounts -- the generous
  // direction, and the opposite of the harsh miscount these single-number
  // lines replaced. 4x/week also sits above the entire density range the
  // anchors were fitted to (see the comment above BANDS in
  // api/signals/training_band.py).
  frBandOccasionalDetail: 'A few times a month',
  frBandSteadyDetail: 'Twice a week',
  frBandCommittedDetail: 'Three times a week',
  frBandDedicatedDetail: 'Five or more, most weeks',
  // Byte-identical to frPhotoSkip's text, under its own symbol. frPhotoSkip is
  // orphaned (declared in both decks, referenced by no screen since the photo
  // step left first run, FR-D31); reusing or renaming it would imply that
  // block is being dealt with, and it is not.
  frBandSkip: 'Skip for now',

  // ── First run, the last screen (FR-D26) ──
  // Deliberately short and WITHOUT the mechanism: the coach's first greeting on
  // Home says what the answers bought (FR-D6), and two payoffs in a row is one
  // payoff and one echo. iOS-only for now.
  frReadyTitle: 'Ready to go.',
  frReadyBody: "I've got what I need. Log your first session and I'll start reading it.",
  frReadyCta: "Let's train",

  // ── First run, the greeting (FR-D28) ──
  // Straight after the name, before the body questions: the one screen that
  // uses the name it just learned. Exclamation marks are allowed HERE and not
  // in the questions, the same way BitePal's framing screens say "Got it!"
  // while its questions stay flat. iOS-only for now.
  frGreetingTitle: 'Hi {first}!',
  frGreetingBody:
    'Looking forward to being your coach. I just need to ask you a few things first.',
  frGreetingCta: 'Ask away',

  // ── First run, the summary (FR-D29) ──
  // Prose, not a table: BitePal's summaries read "Losing 3 kg is a realistic
  // target", with the value inline and coloured. {age} and {height} render in
  // amber on iOS. iOS-only for now.
  frSummaryTitle: "Okay {first}, so you're {age} and {height} tall.",
  // Gender is collected (step 4) but deliberately not said back here -- the
  // product decision, made after the gender line shipped and was reviewed,
  // is that this screen doesn't speak to gender at all. Removed 2026-08-24,
  // in the same pass that added the band line below.
  //
  // The band line replaces it as the summary's second line. Four sentences
  // rather than one template, same reasoning as the gender line this
  // replaces had: "committed, so I'll expect a few times a month" is not a
  // sentence for any band but its own. `frBandSkip`'s reasoning applies here
  // too -- a skipped band still needs a line, so it isn't silently absent
  // from the one screen whose whole job is reflecting the answers back.
  frSummaryBandOccasional:
    "Occasional, so I'll expect a few times a month until you show me otherwise.",
  frSummaryBandSteady: "Steady, so I'll expect twice a week until you show me otherwise.",
  frSummaryBandCommitted:
    "Committed, so I'll expect three times a week until you show me otherwise.",
  frSummaryBandDedicated:
    "Dedicated, so I'll expect five or more, most weeks, until you show me otherwise.",
  frSummaryBandSkipped:
    "You skipped that one, and that's fine. I'll learn your training rhythm from what you log.",
  frSummaryCta: 'Looks good',

  frPhotoTitle: 'Want to add a photo?',
  frPhotoReason: 'Totally optional. Your initials look good too.',
  frPhotoChoose: 'Choose a profile photo',
  frPhotoFinish: 'Finish',
  frPhotoSkip: 'Skip for now',
  frPhotoRemove: 'Remove photo',
  frPhotoErrorUpload: "That photo didn't upload. Try another one, or skip for now.",
  frPhotoErrorSize: 'That image is too big. Pick one under 8 MB.',
  frPhotoErrorHeic:
    "iPhone photos in HEIC format aren't supported yet. Choose a JPEG or PNG, or take a screenshot of the photo.",

  // ── Notifications: the promise (FR-D9, #271) ──
  // The rest of the notifications priming is iOS-only, like the welcome
  // strings: the phone asks for notifications before a first workout, and the
  // web sends none, so those strings live in FirstRunCopy.swift alone. The
  // promise is the one the web carries too. The reach-out (ADR 0007) changed
  // it, and it is what the app says it will and will not send, so both decks
  // state it and scripts/check_copy_decks.py holds them to one wording
  // (notifPromise is a required shared symbol).
  notifPromise:
    "Besides rest alerts, your coach sends at most one message when you've been away. No notification is ever marketing. You can turn that one off in Settings.",

  // ── Account ──
  accountSection: 'Account',
  accountDeleteRow: 'Delete account',
  accountDeleteTitle: 'Delete your account?',
  // This string sits on the confirmation sheet at the moment a user decides whether to delete, so
  // it has to be true. It was corrected once already (a fresh invite restored an account, signing
  // in with Apple alone did not) and is replaced by #365: an account pending deletion is brought
  // back by "Keep my account" (`deletionKeep`, POST /api/me/keep), after signing in again, and by
  // nothing else. It names no provider (there are two) and no invite (none restores an account).
  accountDeleteBody:
    'Your training history, body scans and coach conversations are removed after 30 days. Until then, signing in again and choosing Keep my account brings everything back. Nothing is deleted yet.',
  accountDeleteSignout: "You'll be signed out on every device straight away.",
  accountDeleteConfirm: 'Delete account',
  accountDeleteCancel: 'Cancel',
  // Leaving the account (#1012), the same words on all three clients. `accountDeleteFailed` is the line a deletion that failed
  // shows when the server gave no sentence of its own. The sign-out confirmation's body names no provider (ruling R1 of run 1011:
  // the person may have signed in with Apple or with Google). The web signs out without a dialog, so it carries the title, the
  // body, the hint and the cancel only for the phones.
  accountDeleteFailed: "That didn't go through. Check your connection and try again.",
  accountSignOut: 'Sign out',
  accountSignOutTitle: 'Sign out of Groundwork?',
  accountSignOutBody: 'Your training data stays on the server. You will need to sign in again.',
  accountSignOutHint: "Removes this device's session. Your data stays on the server.",
  accountSignOutCancel: 'Cancel',

  // ── Sign-in surfaces (#104's decisions, SI-D2 to SI-D8; built from #360) ──
  // Shared with iOS (FirstRunCopy.swift) byte for byte; Option B, "The door remembers". The
  // paused trio is the refusal when a sign-in's verified email belongs to another account
  // (owner ruling S-O21: emails are unique, nothing is linked, no second account is made).
  // `signInContextPick` and `signInContinueGoogle` are the two the draft table (the decisions file)
  // had beyond these: iOS shows the first after `signInContext` and both clients show the second.
  signInContext: "New here or coming back, it's the same step.",
  signInContextPick: 'Pick the account you want your training kept under.',
  signInWelcomeBack: 'Welcome back.',
  signInWelcomeBackBody: 'Sign in the way you did last time and your record is right here.',
  signInLastUsed: 'Last used',
  signInContinueApple: 'Continue with Apple',
  signInContinueGoogle: 'Continue with Google',
  signInTerms: 'By continuing you agree to the Terms and the Privacy Policy.',
  pausedTitle: "There's already a Groundwork account for this email.",
  pausedBody:
    'Sign in the way you did before to get back to it. You can add this sign-in method later, in Settings in the Groundwork app.',
  pausedBack: 'Sign in another way',

  // ── Web only ──
  // The sign-in service's SIGN_UP_CLOSED, in our words: the page never prints text a URL carries.
  signInSignUpClosed:
    "Sign-up isn't open yet. If you already have a Groundwork account, sign in the way you did before.",
  // The one line for a sign-in that did not start or finish: the sign-in page and the invite page say it alike.
  signInFailed: "Sign-in didn't go through. Give it another try.",
  accountSignOutFailed: "Couldn't sign out. Check your connection and try again.",

  // The sign-in service's two refusals of an Apple sign-in that a person can act on (#362;
  // iOS shows each as a title and a body). The web's dialog shows the paused trio above for the
  // second (#360) and has no screen for the first: the service signs a person with no email in
  // under an address of its own, so it does not arise there. Apple
  // puts an email in the ID token only for an authorization that asked for the email scope,
  // so someone who authorized before the scope was asked for has none; and an email that
  // belongs to another account is refused with nothing linked (S-O21).
  authNoEmailTitle: 'Apple did not share your email',
  authNoEmailBody:
    'Groundwork needs an email address to set up your sign-in. In Settings, open your name, then Sign-In & Security, then Sign in with Apple, and remove Groundwork. Then sign in again and choose to share your email.',
  authEmailElsewhereTitle: 'That email already has an account',
  authEmailElsewhereBody:
    'The email address on this Apple ID already belongs to a Groundwork account. Sign in the way you did when you made it.',

  // ── Scheduled for deletion (#365, SI-D11; B-ios-deletion and B-web-deletion) ──
  // The full screen an account pending deletion meets after signing in, shared with iOS
  // (FirstRunCopy.swift) byte for byte. Its eyebrow is `accountSection`. The date and the days
  // left are the server's (`MeOut.pending_deletion`); `{days}` is filled, the singular and the last
  // day have a line of their own (not drafted in the decisions file: its own words, "1 day left"
  // and "Today", are used). "Keep my account" is the only way back; signing in never is.
  deletionTitle: 'Your account is scheduled for deletion.',
  deletionDateLabel: 'Deletion date',
  deletionDaysLeft: '{days} days left',
  deletionDaysLeftOne: '1 day left',
  deletionDaysLeftToday: 'Today',
  deletionKeptLabel: 'Still here until then',
  deletionKeptTraining: 'Your training history',
  deletionKeptScans: 'Your body scans',
  deletionKeptCoach: 'Your coach conversations',
  deletionKeep: 'Keep my account',
  deletionSignOut: 'Sign out',
  deletionCaption:
    'Signing out leaves the deletion in place. If you keep your account, your other devices stay signed out until you sign in on them again.',
  deletionKeepFailed: "Couldn't keep your account. Check your connection and try again.",
  // ── Sign-in and devices (#104's decisions SI-D12 to SI-D21; built from #367) ──
  // Shared with iOS (FirstRunCopy.swift) byte for byte, except the lines marked otherwise. The
  // service answers a kind of device and a client as ids (auth/src/device-description.mjs); each id
  // has its words here, and a test on each client fails when an id has none.
  accessTitle: 'Sign-in and devices',
  methodsSection: 'Ways to sign in',
  providerApple: 'Apple',
  providerGoogle: 'Google',
  methodAdded: 'Added',
  methodNotAdded: 'Not added',
  devicesSection: 'Signed in on',
  deviceThis: 'This device',
  deviceActiveToday: 'Active today',
  // SI-D18 drafted "Active today" and "Active {days} days ago" only; a day ago needs its own words.
  deviceActiveYesterday: 'Active yesterday',
  deviceActiveDays: 'Active {days} days ago',
  // One string for the line under a device's name, so the two clients cannot order its parts differently.
  deviceDetail: '{client} · {active}',
  deviceKindIphone: 'iPhone',
  deviceKindIpad: 'iPad',
  deviceKindMac: 'Mac',
  deviceKindWindows: 'Windows PC',
  deviceKindLinux: 'Linux PC',
  deviceKindAndroid: 'Android phone',
  deviceKindOther: 'Another device',
  clientApp: 'Groundwork app',
  clientChrome: 'Chrome',
  clientSafari: 'Safari',
  clientFirefox: 'Firefox',
  clientEdge: 'Edge',
  clientBrowser: 'Browser',
  clientOther: 'Unknown client',
  deviceSignOut: 'Sign out',
  deviceSignOutCancel: 'Cancel',
  deviceSignOutTitle: 'Sign out {device}?',
  deviceSignOutTitleOther: 'Sign out that device?',
  // The body starts with the client, and a client needs its article there ("The Groundwork app"),
  // so there is one body for a named browser and one each for the app, an unnamed browser and a
  // device the service could not name.
  deviceSignOutBody:
    '{client} on that {device} loses access straight away. Whoever uses it next has to sign in again.',
  deviceSignOutBodyApp:
    'The Groundwork app on that {device} loses access straight away. Whoever uses it next has to sign in again.',
  deviceSignOutBodyBrowser:
    'The browser on that {device} loses access straight away. Whoever uses it next has to sign in again.',
  deviceSignOutBodyOther:
    'That device loses access straight away. Whoever uses it next has to sign in again.',
  devicesSignOutOthers: 'Sign out everywhere else',
  devicesNote:
    'A signed-out device loses access straight away, and a phone stops getting notifications. A device that goes unused for 90 days signs itself out.',
  devicesSignOutFailed: "Couldn't sign that device out. Check your connection and try again.",
  // Web only: the tab's line under the page title, and the methods' pointer to the phone (SI-D21).
  accessBlurb: 'How you get into Groundwork, and everywhere you are signed in right now.',
  methodsWebNote:
    'To add or remove a sign-in method, open Settings in the Groundwork app. The web shows them and leaves the changing to your phone.',
  methodLastUsedBrowser: 'Last used in this browser',
  // iOS only: methodLastUsedPhone (shown when the phone remembers a provider, #363), and devicesLoadFailed,
  // devicesRetry and methodsLoadFailed (the web's panels say it with the house error card, which has its
  // own "Try again").

  // ── Consent (#399) ──
  // The consents' words: byte for byte the tables of "The final strings" in
  // docs/superpowers/specs/2026-10-09-consent-screens-decisions.md (CS-D1 to CS-D20, decided by the
  // owner on 2026-10-09, rulings R15 and R20), and byte for byte what FirstRunCopy.swift says for
  // every symbol both clients hold. A symbol only one client shows is in that client's deck alone:
  // the Health ask (consentHealth*, minus the full text) is iOS's, because Health data is agreed to
  // on the phone (CS-D4). CONSENT_TEXT_VERSION, after this object, says which version of each text
  // this deck holds.
  // ── Consent: the asks ──
  // Two short asks as steps of first run (CS-D1): the AI step on both clients, the Health step on
  // iOS alone (CS-D4: the web asks the AI consent only, Health data is agreed to on the phone).
  // `consentChangeable` carries the version of the text shown, `{n}`, which each client fills
  // from `ConsentTextVersion` below. `consentHealthWatch` follows `consentHealthBody` in the same
  // paragraph and is shown only when a Watch is paired with the phone (CS-D20, ruling R20).
  // `frErrorSave`, above, is the line when an answer cannot be saved, as the document lists it.
  consentAiTitle: 'Let AI models write your coaching?',
  consentAiBody:
    'Your training and health data go to AI models through OpenRouter, a US company, and can leave the EU. No provider may keep it.',
  consentAiDetail: 'What exactly is sent',
  consentAgree: 'Agree',
  consentAiDecline: 'Continue without AI',
  consentChangeable: 'Changeable in Settings · Version {n}',
  consentNotNow: 'Not now',

  // ── Consent: an existing user ──
  // CS-D5: shown once, above the first ask met by an account that finished first run before
  // the consents existed.
  consentBeforeYouGoOn: 'BEFORE YOU GO ON',
  consentExistingLead: 'As a tester you were never asked. Your training stays either way.',

  // ── Consent: version 2 of the AI text ──
  // CS-D17, #404: shown at the launch to everyone not yet asked. Nobody who answered version 1 is asked again
  // (CO-D1, CO-D2): the change is announced, not asked. No build shows it while the server is on version 1.
  consentAiBody2:
    'Your training and health data go to AI models through OpenRouter, on EU servers. No provider may keep it.',

  // ── Consent: Settings ──
  // CS-D8, CS-D9: the Privacy page, one row per consent. Seven strings are the web's alone and are
  // in `firstRun.ts` only: the page's blurb, where each consent was given (`consentWhereIphone`,
  // `consentWhereAndroid`, `consentWhereBrowser`) and what the Health row says, since Health data is
  // agreed to on the phone (`consentHealthWebNote` and `consentHealthWebNoteNo`, CS-D4; FR2-D10 took
  // "iPhone" out of both, because a phone may be an Android one).
  privacyTitle: 'Privacy',
  privacyBlurb: 'What you have agreed to, and since when.',
  consentHealthName: 'Health data',
  consentAiName: 'AI processing',
  consentAgreedOn: 'Agreed {date} · Version {n}',
  consentDeclinedOn: 'Declined {date}',
  // Not drawn: the state after a withdrawal (CS-D9).
  consentWithdrawnOn: 'Withdrawn {date}',
  // Not drawn: the state of a consent nobody has asked about yet (CS-D9).
  consentNeverAsked: 'Not asked yet',
  consentWhereIphone: 'On your iPhone',
  // Where an agreement given on Android reads (FR2-D10); web only, like its two neighbours.
  consentWhereAndroid: 'On your Android phone',
  consentWhereBrowser: 'In this browser',
  consentWithdraw: 'Withdraw',
  consentReadAndAgree: 'Read and agree',
  consentFullTextLink: 'Read the full text',
  // The second sentence is the owner's ruling R2 of conductor run 696 (2026-10-24): what Google received
  // expires 14 months after it was sent, whatever the account does.
  privacyNote:
    'Withdrawing stops it at once. What is already stored stays until you delete your account. What Google Analytics received expires 14 months after it was sent.',
  consentHealthWebNote: 'Agreed to on your phone, where your Health data is. You can withdraw it here.',
  // Not drawn: the web's Health row when it has not been agreed to (CS-D9).
  consentHealthWebNoteNo: 'You can agree on your phone, where your Health data is.',

  // ── Consent: withdrawing ──
  // CS-D10. The sheet's small label is the consent's name in capitals; its filled button is
  // `consentWithdraw`.
  withdrawTitle: 'Withdraw your agreement?',
  withdrawAiStops: "Nothing more goes to AI models, and the coach's chat stops.",
  withdrawHealthStops: 'Groundwork stops reading Apple Health.',
  withdrawHealthIos: 'iOS keeps the permission. You can remove it in the Health app.',
  // Android's pair (FR2-D11), picked by the text the grant was to: Android's Settings › Privacy uses the same two.
  withdrawHealthStopsAndroid: 'Groundwork stops reading Health Connect.',
  withdrawHealthAndroid: 'Android keeps the permission. You can remove it in Health Connect.',
  withdrawStays: 'What is already stored stays until you delete your account.',
  withdrawCancel: 'Keep it',

  // ── Consent: without a consent ──
  // CS-D11 to CS-D15, CS-D19: what the app says where a feature needs a consent that is not
  // given. Three are the web's alone (the dock's link and the dashboard's note, CS-D12 and CS-D14)
  // and are in `firstRun.ts` only.
  aiOffLine: 'Chat needs AI processing.',
  // CO-D3: beside every line above, outside Settings: the way to Settings › Privacy, never the ask itself.
  consentOffSettings: 'Turn it on in Settings',
  aiOffDockLink: 'Open the coach',
  aiOffAnalysisTitle: 'No written analysis.',
  aiOffAnalysisBody: 'AI models write it. That needs your agreement.',
  // Not drawn: a chat turn the server refused for want of the consent (CS-D19).
  aiConsentRefused: "The coach can't answer without your agreement to AI processing.",
  healthOffBody: 'Nothing is read from Health.',

  // ── Consent: the full texts ──
  // CS-D16: four numbered facts (what, who, where, why) and two closing sentences, one tap away
  // from the ask. These are the text a person consents to; `scripts/check_copy_decks.py`
  // fingerprints them per version. `consentFullTextEyebrow` takes the consent's name in capitals
  // as `{name}` and the version as `{n}`.
  consentFullTextEyebrow: '{name} · VERSION {n}',
  consentFullTextTitle: 'The full text',
  consentDone: 'Done',
  consentWhatLabel: 'WHAT',
  consentWhoLabel: 'WHO',
  consentWhereLabel: 'WHERE',
  consentWhyLabel: 'WHY',
  consentAiWhat:
    'Your training record. Your first name, date of birth, height and gender. Body measurements and scans. The events, check-ins and protected areas you record. What you write to the coach, and what it remembers about you. Your Health data too, if you agree to that. Your email address and how you sign in are never sent.',
  consentAiWho:
    'OpenRouter, a company in the United States, hands each request to the company whose AI model answers it. Groundwork tells OpenRouter to use only providers that do not store your data and do not train on it.',
  consentAiWhere:
    'On their servers, which can be outside the European Union, including in the United States.',
  // Version 2 of `consentAiWhere`, not drawn (CS-D17, #404).
  consentAiWhere2: 'On servers in the European Union.',
  consentAiWhy:
    "To write your coach's replies, suggestions and short notes, and your weekly analysis. And to sort what you enter: exercise names, events and the coach's own notes about you. Never for advertising, and never sold.",
  consentAiNo:
    "If you say no, you keep logging, your history and every number Groundwork works out by itself. The coach's chat and the written analysis stay off.",
  consentAiLater:
    'You can withdraw this at any time in Settings, under Privacy. Nothing more is sent from then on. What is already stored stays until you delete it or your account.',
  consentHealthWhat:
    'From Apple Health: sleep, heart rate, heart-rate variability, resting heart rate, blood oxygen, VO2 max, steps, active and resting energy, body weight, and your workouts with their distance. The last 90 days first, then each new day. During a workout, the heart rate from your Watch, if you wear one.',
  consentHealthWho:
    'Groundwork Sports UG, the company behind this app. Its server is rented from Hetzner, and traffic to it passes through Cloudflare. AI models get this data only if you also agree to AI processing.',
  consentHealthWhere: 'On that server, in Germany, with the rest of your account.',
  consentHealthWhy:
    'To show how recovered you are, to set your heart-rate zones and rest timer, and to show your body weight and the workouts you did outside Groundwork. If you wear a Watch, the same connection lets it record each session to Apple Health.',
  consentHealthNo:
    'If you say no, nothing is read from Health and Groundwork does not ask iOS for access. A Watch then cannot record your sessions. Everything else works.',
  consentHealthLater:
    'You can withdraw this at any time in Settings, under Privacy. Reading stops at once. What is already stored stays until you delete your account.',
  // The three lines of Android's Health text that differ from the ones above (AL-D7; `consentHealthWho`, `Where` and `Later`
  // are shared). The Privacy tab shows the full text of the text the person answered (FR2-D10), so the web carries these,
  // byte for byte what Android's deck says. The rest of Android's ask is Android's alone.
  consentHealthWhatAndroid:
    'From Health Connect: sleep, heart rate, heart-rate variability, resting heart rate, blood oxygen, VO2 max, steps, active and total calories, body weight, and your workouts with their distance. The last 30 days first, then each new day.',
  consentHealthWhyAndroid:
    'To show how recovered you are, to set your heart-rate zones and rest timer, and to show your body weight and the workouts you did outside Groundwork.',
  consentHealthNoAndroid:
    'If you say no, nothing is read from Health Connect and Groundwork does not ask Android for access. Everything else works.',

  // ── Consent: analytics (#709) ──
  // Version 2 of the analytics text, byte for byte the tables of "The final strings" in
  // docs/superpowers/specs/2026-10-13-analytics-ask-decisions.md (AA-D1 to AA-D9, decided by the owner on
  // 2026-10-24, ruling R1 of conductor run 696). Every string of the text is in FirstRunCopy.swift too, so the
  // version tie and the fingerprint of scripts/check_copy_decks.py hold across the clients; the strings only
  // the web shows (chrome for a browser, a cookie or a sign-in page) are this deck's alone. The landing site
  // (landingpage/src/analytics/copy.ts) holds its own copy of the shared ones (t715).
  // The first layer: the band on the sign-in pages and the signed-in ask (a first-run step, or a dialog).
  consentAnalyticsTitle: 'This site uses cookies',
  consentAnalyticsBody2:
    'Functional cookies make the site work, like the one that keeps you signed in. With your OK, we also use analytics cookies to count which pages are visited, how many people sign up and how the web app is used. Never what you type.',
  consentAnalyticsDetail: 'Cookie details',
  consentAnalyticsAccept: 'Accept all',
  consentAnalyticsDecline: 'Only functional',
  // Web only: the band's footnote, true on the sign-in pages because of their "Cookie settings" link (AA-D7).
  consentAnalyticsChangeable: 'Change this any time under Cookie settings at the foot of the page.',
  consentAnalyticsAccount: 'Kept with your account, in every browser you sign in with.',
  // Web only: the band reopened from "Cookie settings" after a no.
  consentAnalyticsEarlierNo: 'You chose only functional cookies on {date}.',
  consentAnalyticsName: 'Analytics cookies',
  // Cookie details, version 2: the four facts (AA-D3). `consentFullTextEyebrow` takes `consentAnalyticsName`
  // in capitals; the title is `consentAnalyticsDetail`; each part of the WHAT leads with its label, up to the colon.
  consentCookiesFunctionalTitle: 'Functional, always on',
  consentCookiesFunctional:
    'The cookie that keeps you signed in, if you sign in, and the cookie that keeps your answer to this question, for 13 months. Neither goes to anyone outside Groundwork. The site cannot work without them, so they need no agreement.',
  consentCookiesAnalyticsTitle: 'Analytics, optional',
  consentAnalyticsWhatPublic:
    'On the public pages: which pages you open, as addresses with no names, codes or numbers in them; the site you came from, never its address, or, if it was a page of ours, that page with no names, codes or numbers in it; and whether you ask for a place on the waitlist, never the email address you enter.',
  consentAnalyticsWhatApp:
    'In the web app: which pages you open, as addresses with no names, codes or numbers in them; the site you came from, never its address, or, if it was a page of ours, that page with no names, codes or numbers in it; and the campaign name, source and medium of the link you came by, if it had one. When you sign in or sign up, and with which provider where Groundwork knows it. The first-run steps you reach, and that you finished first run. That you opened the workout log and saved a workout, how many sets it had, and whether it was your first; that you messaged the coach; that you opened one of its cards, and which kind; which Settings area you open; and when you agree to analytics, answer the question on AI processing or withdraw your agreement to AI processing. Never what you write or enter.',
  consentAnalyticsWhatAll:
    "Everywhere: a random identifier kept in a cookie in your browser, your browser's language, and what kind of device and browser you use. Google's own tag also reports, by itself, when your visit starts, how many visits there have been, and how long a page is in use. If you are signed in, a pseudonymous identifier made from your account, never your name, email address or account number.",
  consentAnalyticsWho:
    "Google, through Google Analytics, under Google's terms for processing data for Groundwork. Google receives your IP address with each request. It states that for visitors in the EU it uses the address only to estimate a location, and then discards it. Google Analytics' advertising features are off.",
  consentAnalyticsWhere:
    "On Google's servers, which can be outside the European Union, including in the United States. Google keeps it for 14 months.",
  consentAnalyticsWhy2:
    'To see which pages bring people to Groundwork and lead them to sign up, which parts of the web app are used, and where people stop. Never for advertising, and never sold.',
  consentAnalyticsNo:
    'If you choose only functional cookies, the analytics cookies are never set and nothing is loaded from Google. The site works just the same.',
  consentAnalyticsLater2:
    'You can change your answer at any time: under Cookie settings at the foot of the public pages and the sign-in page, or in Settings, under Privacy, once you are signed in. When you withdraw, the website deletes the analytics cookies from your browser and stops loading Google Analytics.',
  consentAnalyticsAccountFull:
    'If you are signed in, your answer is also kept with your account and holds in every browser you sign in with.',
  consentAnalyticsReturns:
    'If your browser keeps no cookies, or deletes them after a few days as Safari can, this question comes back.',

  // ── Consent: analytics, withdrawing ──
  // The withdraw step: a band for a visitor on the sign-in pages (`withdrawAnalyticsStops`, `consentAcceptedOn`),
  // a dialog in Settings once signed in (`withdrawAnalyticsStopsAccount`, `consentAgreedOn`). Its answers are
  // `consentAnalyticsDecline` and `withdrawAnalyticsKeep`, both outlined.
  withdrawAnalyticsTitle: 'Turn off analytics cookies?',
  consentAcceptedOn: 'All accepted {date} · Version {n}',
  withdrawAnalyticsStopsAccount:
    'Google Analytics stops loading in every browser you sign in with, and its cookies are deleted from this one. The page reloads.',
  withdrawAnalyticsStops:
    'The analytics cookies are deleted from this browser, and Google Analytics stops loading on this website. The page reloads.',
  withdrawAnalyticsStays: 'What Google already received stays until it expires, 14 months after it was sent.',
  withdrawAnalyticsKeep: 'Keep all',

  // ── Consent: analytics, Settings and the sign-in pages (web only) ──
  consentWhereBeforeSignIn: 'In this browser, before you signed in',
  cookieSettings: 'Cookie settings',
  withdrawnNow: 'Only functional cookies now. The analytics cookies are deleted.',
  savedAll: 'Saved. All cookies accepted.',
  savedFunctional: 'Saved. Only functional cookies.',
  // A browser that keeps no cookies (AA-D9 (a)): the band says so in place of its sentences.
  consentAnalyticsNotKept:
    "Your browser didn't keep your answer, so this question will come back on the next page. Nothing is loaded from Google.",
  consentAnalyticsNotKeptClose: 'Close',
} as const

/**
 * The version of each consent's text that this deck holds (CS-D7, #399). A client shows the text of
 * one version, and the server's current version (`consents` in `GET /api/me`) is what it is compared
 * with to decide whether to ask again. A grant or a decline is recorded with the version shown, so
 * this number goes to the server in every answer and fills `{n}` in `COPY.consentChangeable`.
 *
 * `scripts/check_copy_decks.py` fails when a number differs from `HEALTH_DATA_VERSION`,
 * `AI_PROCESSING_VERSION` or `ANALYTICS_VERSION` in `api/platform/consent.py` (and, for the analytics text, from
 * `ANALYTICS_TEXT_VERSION` in `analytics/config.ts`, which the wire check reads as text), and when the
 * strings that make up a consent's text
 * (that script names them) change without a new version or a recorded reason. A version is raised
 * when the text changes in substance, never for a typo, and always in three places at once: the
 * server's constant, this one and `ConsentTextVersion` in `FirstRunCopy.swift`. The server goes
 * first (it refuses a version above its own).
 *
 * `healthData` is the number of the Health text iOS and the web show. Android's Health text (the `...Android` lines above)
 * is a text of its own with its own number, `HEALTH_DATA_ANDROID_VERSION`: the Kotlin deck's `healthData` is held to it,
 * and so is `healthDataAndroid` here, because the Privacy tab opens the full text of the Health text a person answered
 * (run 119's ruling R12) and labels it with the number of that text. iOS holds none. The script fails when either is
 * raised without the other.
 *
 * Outside `COPY` on purpose: the deck is strings, and these are numbers. One integer per line so the
 * script can read them as text.
 */
export const CONSENT_TEXT_VERSION = {
  healthData: 1,
  healthDataAndroid: 1,
  aiProcessing: 1,
  analytics: 2,
} as const

/** Substitute `{key}` placeholders. Missing keys are left visible on purpose. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? String(values[key]) : match,
  )
}
